/**
 * Sync API
 * Handles GreytHR synchronization API calls
 */
import apiClient from './apiClient';

export interface SyncResult {
  created: number;
  updated: number;
  deactivated: number;
  errors: string[];
  total_fetched: number;
  started_at: string;
  completed_at: string | null;
  success: boolean;
}

export interface SyncStatus {
  is_running: boolean;
  last_sync: string | null;
  last_result: SyncResult | null;
  sync_enabled: boolean;
  sync_schedule: string;
  token_configured: boolean;
  host_configured: boolean;
  ready: boolean;
}

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  host?: string;
  sample_count?: number;
}

export interface SwipeSyncStatus {
  is_running: boolean;
  total_swipes: number;
  today_swipes: number;
  employees_tracked: number;
  synced_today: number;
  errors: number;
}

export interface SwipeSyncResult {
  success: boolean;
  employees_processed: number;
  total_fetched: number;
  total_created: number;
  total_skipped: number;
  errors: string[];
  date_range: string;
}

export interface LeaveSyncStatus {
  is_running: boolean;
  total_leaves: number;
  pending_leaves: number;
  filled_leaves: number;
}

export interface LeaveSyncResult {
  success: boolean;
  total_fetched: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export const syncApi = {
  /**
   * Trigger GreytHR employee sync
   * Super Admin only
   */
  syncGreythrEmployees: async (): Promise<{ message: string; results: SyncResult }> => {
    return apiClient.post<{ message: string; results: SyncResult }>('/sync/greythr/employees');
  },

  /**
   * Get sync status and last results
   */
  getSyncStatus: async (): Promise<SyncStatus> => {
    return apiClient.get<SyncStatus>('/sync/greythr/status');
  },

  /**
   * Test GreytHR API connection
   */
  testConnection: async (): Promise<ConnectionTestResult> => {
    return apiClient.post<ConnectionTestResult>('/sync/greythr/test');
  },

  // ==================== SWIPE/ATTENDANCE SYNC ====================

  /**
   * Get swipe sync status
   */
  getSwipeSyncStatus: async (): Promise<SwipeSyncStatus> => {
    return apiClient.get<SwipeSyncStatus>('/sync/greythr/swipes/status');
  },

  /**
   * Trigger manual swipe sync with optional date range
   */
  syncSwipes: async (startDate?: string, endDate?: string): Promise<{ message: string; results: SwipeSyncResult }> => {
    const body: any = {};
    if (startDate) body.start_date = startDate;
    if (endDate) body.end_date = endDate;
    return apiClient.post<{ message: string; results: SwipeSyncResult }>('/sync/greythr/swipes', body);
  },

  // ==================== LEAVE SYNC ====================

  /**
   * Get leave sync status
   */
  getLeaveSyncStatus: async (): Promise<LeaveSyncStatus> => {
    return apiClient.get<LeaveSyncStatus>('/sync/greythr/leaves/status');
  },

  /**
   * Trigger leave sync from GreytHR
   */
  syncLeaves: async (): Promise<{ message: string; results: LeaveSyncResult }> => {
    return apiClient.post<{ message: string; results: LeaveSyncResult }>('/sync/greythr/leaves');
  },

  /**
   * Process pending leaves and fill timesheets
   */
  processLeaveTimesheets: async (): Promise<{ message: string; results: any }> => {
    return apiClient.post<{ message: string; results: any }>('/sync/greythr/leaves/process');
  },
};

export default syncApi;
